import classNames from 'classnames'
import { LockKeyholeIcon } from 'lucide-react'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { OrganizationPermission, OrganizationScope } from '@campai/lib-common/entities'

import { Button, InfoBadge, Link, type LinkNavigationProps } from '@/components/new/ui'

import type { LucideIcon } from 'lucide-react'

type EmptyStateProps = {
    small?: boolean
    layout?: 'default' | 'overlay' | 'container'
    Icon?: LucideIcon
    title: string
    subtitle: string | ReactNode
    actionCaption?: string
    actionLinkProps?: LinkNavigationProps
    onAction?: () => void | Promise<void>
    children?: ReactNode
}

export const EmptyState = ({
    small,
    layout,
    Icon,
    title,
    subtitle,
    actionCaption,
    actionLinkProps,
    onAction,
    children,
}: EmptyStateProps) => {
    const [isWorking, setWorking] = useState(false)

    const handleAction = useCallback(async () => {
        if (onAction) {
            setWorking(true)
            await onAction()
            setWorking(false)
        }
    }, [onAction])

    const actionButton = actionLinkProps ? (
        <Link
            size={small ? 'sm' : 'default'}
            className="h-auto max-w-full whitespace-normal break-words px-0 py-0 text-center"
            loading={isWorking}
            {...actionLinkProps}
            onClick={handleAction}
        >
            {actionCaption}
        </Link>
    ) : (
        <Button
            type="button"
            variant="link"
            size={small ? 'sm' : 'default'}
            className="h-auto max-w-full whitespace-normal break-words px-0 py-0 text-center"
            loading={isWorking}
            onClick={() => void handleAction()}
        >
            {actionCaption}
        </Button>
    )

    const element = (
        <div className="flex min-w-0 max-w-full flex-col items-center justify-center max-sm:px-4">
            {Icon && (
                <div
                    className={classNames(
                        'bg-muted text-muted-foreground flex items-center justify-center rounded-full',
                        { 'size-20': !small, 'size-14': small }
                    )}
                >
                    <Icon className={small ? 'size-7' : 'size-10'} strokeWidth={1.5} aria-hidden />
                </div>
            )}
            <div
                className={classNames('max-w-full break-words text-center font-medium', {
                    'mt-6 text-xl': !small,
                    'mt-4 text-base': small,
                })}
            >
                {title}
            </div>
            <div
                className={classNames('text-muted-foreground max-w-[512px] text-center', {
                    'mt-3 text-base': !small,
                    'mt-1': small,
                })}
            >
                {subtitle}
            </div>
            {children && <div className="mt-3">{children}</div>}
            {actionCaption && (onAction || actionLinkProps) && (
                <div
                    className={classNames('w-full max-w-96 text-center font-medium', {
                        'border-divide mt-6 border-t pt-6 text-base': !small,
                        'mt-4': small,
                    })}
                >
                    {actionButton}
                </div>
            )}
        </div>
    )

    if (layout !== 'default') {
        return (
            <div
                className={classNames({
                    'absolute bottom-0 left-0 right-0 top-0 z-50 flex items-center justify-center bg-black/50':
                        layout === 'overlay',
                    'flex h-full w-full items-center justify-center': layout === 'container',
                })}
            >
                {element}
            </div>
        )
    }

    return element
}

const NoAcess = ({
    permissions,
    subtitle,
    ...props
}: Pick<EmptyStateProps, 'small' | 'layout'> & {
    permissions?: Partial<Record<OrganizationScope, Partial<OrganizationPermission>>>
    subtitle?: EmptyStateProps['subtitle']
}) => {
    const { t } = useTranslation()

    const i18nKey = 'components.emptyState.noAccess'

    const requiredPermissions = useMemo(() => {
        if (!permissions) {
            return null
        }

        const getScopeTranslationKey = (scope: OrganizationScope) => {
            switch (scope) {
                case 'contactRule':
                    return 'organization.organizationPermissions.scopes.rule' as const
                default:
                    return `organization.organizationPermissions.scopes.${scope}` as const
            }
        }

        return (
            t(`${i18nKey}.requiredPermissions`) +
            ':\n\n' +
            (Object.keys(permissions) as OrganizationScope[])
                .map((scope) => {
                    return (
                        `• ${t(getScopeTranslationKey(scope))} (` +
                        (Object.keys(permissions[scope] ?? {}) as (keyof OrganizationPermission)[])
                            .map((permission) =>
                                t(`organization.organizationPermissions.scopePermissions.${permission}`)
                            )
                            .join(', ')
                            .toLocaleLowerCase() +
                        `)`
                    )
                })
                .join('\n')
        )
    }, [permissions, t])

    return (
        <EmptyState
            {...props}
            Icon={LockKeyholeIcon}
            title={t(`${i18nKey}.title`)}
            subtitle={
                <div className="flex items-center gap-x-2">
                    {subtitle ?? t(`${i18nKey}.subtitle`)}
                    {requiredPermissions && <InfoBadge severity="info">{requiredPermissions}</InfoBadge>}
                </div>
            }
        />
    )
}

EmptyState.NoAccess = NoAcess
